import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { 
  createOrder, 
  createRazorpayOrder, 
  updateOrderRazorpayId,
  updateOrderPayment, 
  verifyPayment,
  getRazorpayKey 
} from '../utils/api';
import { toast } from 'react-toastify';
import { FiCreditCard, FiArrowLeft, FiAlertCircle, FiTag } from 'react-icons/fi';
import './Checkout.css';

// Base URL for serving static files (images)
const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') return '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  return apiUrl.replace(/\/api\/?$/, '');
};

const SERVICEABLE_PINCODES = [
  '221001', '221002', '221003', '221004', '221005',
  '221006', '221007', '221008', '221009', '221010'
];

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const appliedCoupon = location.state?.coupon || null;
  const [loading, setLoading] = useState(false);
  const [razorpayKey, setRazorpayKey] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [pincodeError, setPincodeError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: 'Uttar Pradesh',
    pincode: '',
    notes: ''
  });

  // Fetch Razorpay key on mount
  useEffect(() => {
    const fetchRazorpayKey = async () => {
      try {
        const response = await getRazorpayKey();
        setRazorpayKey(response.key);
      } catch (error) {
        setPaymentError('Payment system unavailable. Please try again later.');
      }
    };
    fetchRazorpayKey();
  }, []);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (paymentError) setPaymentError(null);

    if (name === 'pincode' && value.length === 6) {
      if (!SERVICEABLE_PINCODES.includes(value)) {
        setPincodeError('Sorry, this pincode is not serviceable.');
      } else {
        setPincodeError('');
      }
    } else if (name === 'pincode') {
      setPincodeError('');
    }
  };

  const validateForm = () => {
    const { name, email, phone, street, city, state, pincode } = formData;

    if (!name || !email || !phone || !street || !city || !state || !pincode) {
      toast.error('Please fill in all required fields');
      return false;
    }

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    if (!/^[0-9]{10}$/.test(phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return false;
    }

    if (!/^[0-9]{6}$/.test(pincode)) {
      toast.error('Please enter a valid 6-digit pincode');
      return false;
    }

    if (!SERVICEABLE_PINCODES.includes(pincode)) {
      toast.error('Sorry, this pincode is not serviceable.');
      setPincodeError('Sorry, this pincode is not serviceable.');
      return false;
    }

    return true;
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/60x60?text=No+Image';
    if (imageUrl.startsWith('/uploads/')) {
      return `${getBaseUrl()}${imageUrl}`;
    }
    return imageUrl;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    if (!razorpayKey) {
      toast.error('Payment system is not ready. Please refresh and try again.');
      return;
    }

    setLoading(true);
    setPaymentError(null);

    try {
      const subtotal = getCartTotal();
      const discountAmount = appliedCoupon ? Math.round(subtotal * appliedCoupon.discount) : 0;
      const afterDiscount = subtotal - discountAmount;
      const shipping = afterDiscount >= 999 ? 0 : 50;
      const total = afterDiscount + shipping;

      // Step 1: Create order in our database
      const orderData = {
        customerInfo: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            pincode: formData.pincode,
            country: 'India'
          }
        },
        items: cart.map(item => ({
          product: item._id,
          productName: item.name,
          productImage: item.imageUrl,
          quantity: item.quantity,
          price: item.price,
          variantId: item.variantId || null,
          variantWeight: item.weight || null
        })),
        subtotal: subtotal,
        shippingAmount: shipping,
        discountAmount: discountAmount,
        totalAmount: total,
        notes: formData.notes
      };

      const orderResponse = await createOrder(orderData);
      const order = orderResponse.order;

      // Step 2: Create Razorpay order
      const razorpayOrderResponse = await createRazorpayOrder(
        total,
        'INR',
        order.orderNumber,
        { 
          orderNumber: order.orderNumber,
          customerEmail: formData.email 
        }
      );
      const razorpayOrder = razorpayOrderResponse.order;

      // Step 3: Link Razorpay order to our order
      await updateOrderRazorpayId(order._id, razorpayOrder.id);

      // Step 4: Open Razorpay Checkout
      const options = {
        key: razorpayKey,
        amount: razorpayOrder.amount, // Amount in paise
        currency: razorpayOrder.currency,
        name: 'Pawkit',
        description: `Order #${order.orderNumber}`,
        order_id: razorpayOrder.id,
        handler: async function (response) {
          // This function is called on successful payment
          try {
            // Verify payment signature
            const verifyResponse = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderId: order._id
            });

            if (verifyResponse.success) {
              // Update order with payment details
              await updateOrderPayment(order._id, {
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
                paymentStatus: 'completed',
                paymentMethod: 'razorpay'
              });

              // Success! Clear cart and redirect
              clearCart();
              toast.success('Payment successful! Your order has been placed.');
              navigate(`/order-success/${order.orderNumber}`);
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            setPaymentError('Payment verification failed. Please contact support with order number: ' + order.orderNumber);
            toast.error('Payment verification failed. Please contact support.');
            
            // Update order with failed status
            await updateOrderPayment(order._id, {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              paymentStatus: 'failed'
            });
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email
        },
        theme: {
          color: '#0A0A0A'
        },
        modal: {
          ondismiss: async function() {
            setPaymentError('Payment was cancelled. You can try again.');
            toast.info('Payment cancelled. You can try again when ready.');
            
            // Update order to show payment was not completed
            await updateOrderPayment(order._id, {
              paymentStatus: 'failed'
            });
            setLoading(false);
          },
          confirm_close: true,
          escape: false
        },
        retry: {
          enabled: true,
          max_count: 3
        }
      };

      // Check if Razorpay is loaded
      if (typeof window.Razorpay === 'undefined') {
        throw new Error('Razorpay SDK failed to load. Please check your internet connection.');
      }

      const razorpay = new window.Razorpay(options);
      
      // Handle payment failure
      razorpay.on('payment.failed', async function (response) {
        setPaymentError(`Payment failed: ${response.error.description}`);
        toast.error(`Payment failed: ${response.error.description}`);
        
        await updateOrderPayment(order._id, {
          razorpayOrderId: response.error.metadata?.order_id,
          razorpayPaymentId: response.error.metadata?.payment_id,
          paymentStatus: 'failed'
        });
        setLoading(false);
      });

      razorpay.open();

    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Checkout failed. Please try again.';
      setPaymentError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const subtotal = getCartTotal();
  const discountAmount = appliedCoupon ? Math.round(subtotal * appliedCoupon.discount) : 0;
  const afterDiscount = subtotal - discountAmount;
  const shipping = afterDiscount >= 999 ? 0 : 50;
  const total = afterDiscount + shipping;

  if (cart.length === 0) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="empty-checkout">
            <h2>Your cart is empty</h2>
            <p>Add some products before checkout</p>
            <Link to="/shop" className="btn btn-primary">
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-header">
          <Link to="/cart" className="back-link">
            <FiArrowLeft /> Back to Cart
          </Link>
          <h1 className="page-title">Checkout</h1>
        </div>

        {paymentError && (
          <div className="payment-error-banner">
            <FiAlertCircle />
            <span>{paymentError}</span>
            <button onClick={() => setPaymentError(null)}>Dismiss</button>
          </div>
        )}

        <div className="checkout-content">
          {/* Checkout Form */}
          <div className="checkout-form">
            <div className="form-section">
              <h2>Contact Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <div className="phone-input">
                    <span className="phone-prefix">+91</span>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="10-digit number"
                      maxLength="10"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h2>Delivery Address</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    placeholder="House no., Building name, Street"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="6-digit pincode"
                    maxLength="6"
                    required
                    className={pincodeError ? 'input-error' : ''}
                  />
                  {pincodeError && (
                    <span className="pincode-error">{pincodeError}</span>
                  )}
                </div>
                <div className="form-group full-width">
                  <label>Delivery Instructions (Optional)</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Landmark, delivery time preference, etc."
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="order-summary">
            <h2>Order Summary</h2>

            <div className="summary-items">
              {cart.map((item, index) => (
                <div key={`${item._id}-${item.variantId || index}`} className="summary-item">
                  <img src={getImageUrl(item.imageUrl)} alt={item.name} />
                  <div className="item-details">
                    <p className="item-name">{item.name}</p>
                    <p className="item-meta">Qty: {item.quantity}</p>
                  </div>
                  <p className="item-price">₹{(item.price * item.quantity).toLocaleString()}</p>
                </div>
              ))}
            </div>

            {appliedCoupon && (
              <div className="checkout-coupon-badge">
                <FiTag />
                <span>Coupon <strong>{appliedCoupon.code}</strong> applied — {appliedCoupon.label}</span>
              </div>
            )}

            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString()}</span>
              </div>
              {appliedCoupon && (
                <div className="summary-row discount-row">
                  <span>Discount ({appliedCoupon.label})</span>
                  <span>−₹{discountAmount.toLocaleString()}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Shipping</span>
                <span className={shipping === 0 ? 'free' : ''}>
                  {shipping === 0 ? 'FREE' : `₹${shipping}`}
                </span>
              </div>
              {shipping === 0 && (
                <div className="free-shipping-note">
                  You saved ₹50 on shipping!
                </div>
              )}
              <div className="summary-row total">
                <span>Total</span>
                <span>₹{total.toLocaleString()}</span>
              </div>
            </div>

            <button
              onClick={handlePayment}
              className="btn btn-primary pay-btn"
              disabled={loading || !razorpayKey}
            >
              <FiCreditCard />
              {loading ? 'Processing...' : `Pay ₹${total.toLocaleString()}`}
            </button>

            <p className="payment-note">
              Payments are processed securely by Razorpay
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
